// "New meeting" dialog for My Calendar — the in-app way to schedule a personal
// meeting (previously only Dana could create one). Saves via leave-service
// POST /events/ under the signed-in user; invited attendees get an email and
// see it on their own calendar. Backend rejects past start times.
//
// Attendees: type-to-search the company employee directory (name or email,
// picked entries become chips) — or free-type any external email address.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/features/shared/components/ui/dialog';
import { Loader2, X } from 'lucide-react';
import {
  createMyEvent, updateMyEvent, getMyEvent, getEmployees, getCalendarSyncStatus, getCalendarConnectUrl,
  type CalendarSyncStatus,
} from '@/lib/hrApi';
import { getApiErrorMessage } from '@/lib/apiError';

const pad = (n: number) => String(n).padStart(2, '0');
const EMAIL_RE = /^\S+@\S+\.\S+$/;

interface Person { email: string; name?: string; internal?: boolean; role?: 'participant' | 'co_host' }

/** When set, the dialog edits this existing meeting instead of creating one. */
export interface EditingMeeting {
  id: string;
  title: string;
  startAt: string;
  endAt?: string;
  location?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  editing?: EditingMeeting | null;
}

export const NewMeetingDialog: React.FC<Props> = ({ open, onClose, onCreated, editing }) => {
  const tomorrow = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }, []);
  const [title, setTitle] = useState('');
  const [date, setDate] = useState(tomorrow);
  const [time, setTime] = useState('09:00');
  const [duration, setDuration] = useState(30);
  const [location, setLocation] = useState('');
  const [saving, setSaving] = useState(false);
  // Video-link generation (create only): offered when the organizer's calendar
  // is connected; when it isn't but the tenant has a standard provider, nudge
  // them to connect it.
  const [sync, setSync] = useState<CalendarSyncStatus | null>(null);
  const [wantVideoLink, setWantVideoLink] = useState(true);
  const syncProvider = sync?.connected ? sync.provider : null;
  const tenantProvider = sync?.enabled && sync.tenant_meeting_provider && sync.tenant_meeting_provider !== 'none'
    ? sync.tenant_meeting_provider : null;
  // Org credentials (Phase 2): links work for every organizer, no personal
  // connection required — the tenant provider decides the tool.
  const orgLinks = Boolean(sync?.org_meeting_links && tenantProvider);
  const linkProvider = orgLinks ? tenantProvider : syncProvider;

  // Attendee picker state (declared before the prefill effect that seeds it).
  const [attendees, setAttendees] = useState<Person[]>([]);
  // In edit mode, only send attendees when the current list actually loaded —
  // otherwise a failed load would read as "remove everyone".
  const [attendeesLoaded, setAttendeesLoaded] = useState(false);
  // Organizer delegation/transfer: null = me (or unchanged in edit mode). The
  // organizer owns the meeting — provider link + calendar follow them.
  const [organizer, setOrganizer] = useState<Person | null>(null);
  const [orgPickerOpen, setOrgPickerOpen] = useState(false);
  const [orgQuery, setOrgQuery] = useState('');
  const [orgOpts, setOrgOpts] = useState<Person[]>([]);
  const [myRole, setMyRole] = useState<'organizer' | 'creator' | 'attendee'>('organizer');

  // Prefill when opened in edit mode — including the current attendee list
  // (the aggregated calendar feed omits it), so a forgotten invitee can be
  // added after the fact.
  useEffect(() => {
    if (!open) return;
    if (editing) {
      const start = new Date(editing.startAt);
      const end = editing.endAt ? new Date(editing.endAt) : new Date(start.getTime() + 30 * 60000);
      setTitle(editing.title);
      setDate(`${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`);
      setTime(`${pad(start.getHours())}:${pad(start.getMinutes())}`);
      setDuration(Math.max(15, Math.round((end.getTime() - start.getTime()) / 60000)));
      setLocation(editing.location || '');
      setAttendees([]);
      setAttendeesLoaded(false);
      setOrganizer(null); setOrgPickerOpen(false); setOrgQuery('');
      getMyEvent(editing.id)
        .then((ev) => {
          setAttendees((ev.attendees || []).map((a) => ({ email: a.email, name: a.name, role: a.role })));
          setAttendeesLoaded(true);
          setMyRole(ev.role || 'organizer');
        })
        // Leave attendees unsent on failure — never wipe the list blind.
        .catch(() => setAttendeesLoaded(false));
    } else {
      setTitle(''); setDate(tomorrow); setTime('09:00'); setDuration(30); setLocation('');
      setAttendees([]);
      setAttendeesLoaded(true);
      setOrganizer(null); setOrgPickerOpen(false); setOrgQuery('');
      setMyRole('organizer');
    }
  }, [open, editing, tomorrow]);

  // Organizer search (internal directory only — the organizer must be a real
  // employee; external emails can be participants, never organizers).
  const oq = orgQuery.trim().toLowerCase();
  useEffect(() => {
    if (!oq || oq.length < 2) { setOrgOpts([]); return; }
    let active = true;
    const timer = setTimeout(() => {
      getEmployees({ search: oq, page: 1, page_size: 8 })
        .then((r: any) => {
          if (!active) return;
          setOrgOpts(((r?.items as any[]) || []).filter((e) => e.email).map((e) => ({
            email: String(e.email).toLowerCase(),
            name: `${e.first_name ?? ''} ${e.last_name ?? ''}`.trim() || String(e.email),
            internal: true,
          })));
        })
        .catch(() => { if (active) setOrgOpts([]); });
    }, 250);
    return () => { active = false; clearTimeout(timer); };
  }, [oq]);

  useEffect(() => {
    if (!open || editing) return;
    getCalendarSyncStatus().then(setSync).catch(() => setSync(null));
  }, [open, editing]);

  const connectCalendar = async () => {
    if (!tenantProvider) return;
    try {
      const url = await getCalendarConnectUrl(tenantProvider);
      window.open(url, '_blank', 'noopener');
    } catch { /* provider unavailable — nudge stays */ }
  };

  const [query, setQuery] = useState('');
  const [directory, setDirectory] = useState<Person[]>([]);
  const [focusIdx, setFocusIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Server-side directory search (scales to any headcount — never downloads
  // the whole directory): debounce the term, ask the API for the top matches.
  // Failure just means no suggestions; external emails still work by typing.
  const q = query.trim().toLowerCase();
  useEffect(() => {
    if (!q || q.length < 2) { setDirectory([]); return; }
    let active = true;
    const timer = setTimeout(() => {
      getEmployees({ search: q, page: 1, page_size: 20 })
        .then((r: any) => {
          if (!active) return;
          setDirectory(
            ((r?.items as any[]) || [])
              .filter((e) => e.email)
              .map((e) => ({
                email: String(e.email).toLowerCase(),
                name: `${e.first_name ?? ''} ${e.last_name ?? ''}`.trim() || String(e.email),
                internal: true,
              })),
          );
        })
        .catch(() => { if (active) setDirectory([]); });
    }, 250);
    return () => { active = false; clearTimeout(timer); };
  }, [q]);

  const suggestions = useMemo(() => {
    if (!q) return [];
    return directory
      .filter((p) => !attendees.some((a) => a.email === p.email))
      .slice(0, 6);
  }, [q, directory, attendees]);
  const externalCandidate = EMAIL_RE.test(q) && !attendees.some((a) => a.email === q)
    && !suggestions.some((s) => s.email === q) ? q : null;
  const optionCount = suggestions.length + (externalCandidate ? 1 : 0);

  const addAttendee = (p: Person) => {
    setAttendees((prev) => (prev.some((a) => a.email === p.email) ? prev : [...prev, p]));
    setQuery('');
    setFocusIdx(0);
    inputRef.current?.focus();
  };
  const removeAttendee = (email: string) =>
    setAttendees((prev) => prev.filter((a) => a.email !== email));

  const commitFocused = () => {
    if (focusIdx < suggestions.length && suggestions[focusIdx]) return addAttendee(suggestions[focusIdx]);
    if (externalCandidate) return addAttendee({ email: externalCandidate });
    if (suggestions.length === 1) return addAttendee(suggestions[0]);
  };

  const onPickerKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setFocusIdx((i) => Math.min(i + 1, optionCount - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setFocusIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && (optionCount > 0)) { e.preventDefault(); commitFocused(); }
    else if ((e.key === ',' || e.key === ' ') && EMAIL_RE.test(q)) { e.preventDefault(); addAttendee({ email: q }); }
    else if (e.key === 'Backspace' && !query && attendees.length) { removeAttendee(attendees[attendees.length - 1].email); }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !date || !time) return;
    setSaving(true);
    try {
      const start = new Date(`${date}T${time}:00`);
      const end = new Date(start.getTime() + duration * 60000);
      // Offset-aware local time — zone-less strings were stored as UTC, so a
      // CDT user's 17:00 became 12:00 local (timezone program 2026-08).
      const fmt = (d: Date) => {
        const off = -d.getTimezoneOffset();
        const sign = off >= 0 ? '+' : '-';
        const abs = Math.abs(off);
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
      };
      let browserZone: string | undefined;
      try { browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined; } catch { /* zone-less */ }
      // A valid email still sitting in the input counts too.
      const pending = EMAIL_RE.test(q) && !attendees.some((a) => a.email === q) ? [{ email: q }] : [];
      const invitees = [...attendees, ...pending].map(({ email, name, role }: Person) => ({ email, name, role }));
      if (editing) {
        await updateMyEvent(editing.id, {
          title: title.trim(),
          start_at: fmt(start),
          end_at: fmt(end),
          location: location.trim(),
          // Only send the set when the current list loaded — a failed load must
          // not read as "remove everyone".
          ...(attendeesLoaded ? { attendees: invitees } : {}),
          // Organizer transfer only when explicitly picked (audited server-side).
          ...(organizer ? { organizer_email: organizer.email } : {}),
        });
        toast.success('Meeting updated');
      } else {
        const wantLink = Boolean(linkProvider) && wantVideoLink && !location.trim();
        const created = await createMyEvent({
          title: title.trim(),
          start_at: fmt(start),
          end_at: fmt(end),
          meeting_timezone: browserZone,
          organizer_timezone: browserZone,
          location: location.trim() || undefined,
          attendees: invitees.length ? invitees : undefined,
          generate_meet_link: wantLink,
          // Delegation (EA → manager): the organizer owns the meeting, the
          // link and the calendar event; you stay the creator.
          organizer_email: organizer?.email,
        });
        toast.success(organizer ? `Meeting scheduled for ${organizer.name || organizer.email}` : 'Meeting scheduled');
        // Link minting is best-effort/fail-soft on the server: if we asked for a
        // video link but the response came back without one (org creds missing,
        // calendar scope not granted, provider hiccup), say so — otherwise the
        // ticked box silently yields a link-less meeting.
        const gotLink = /^https?:\/\//i.test((created?.location || '').trim());
        if (wantLink && !gotLink) {
          toast(
            `Meeting created, but the ${linkProvider === 'microsoft' ? 'Teams' : 'Meet'} link couldn’t be added automatically. Open the meeting and paste a link in Location.`,
            { icon: '⚠️', duration: 8000 },
          );
        }
      }
      setTitle(''); setLocation(''); setAttendees([]); setQuery('');
      onCreated();
      onClose();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not schedule the meeting'), { duration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full h-9 px-3 rounded-lg border border-border bg-card text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-orange-300';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit meeting' : 'New meeting'}</DialogTitle>
          <DialogDescription>
            {editing
              ? 'Change the details or invitees — added people get an invite, removed people a notice, and your connected calendar is updated automatically.'
              : 'Schedule a meeting on your calendar. Invited attendees get an email and see it on theirs.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label htmlFor="nm-title" className="block text-sm font-medium text-foreground mb-1">Title</label>
            <input id="nm-title" className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={200} placeholder="e.g. Payroll review sync" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-1">
              <label htmlFor="nm-date" className="block text-sm font-medium text-foreground mb-1">Date</label>
              <input id="nm-date" type="date" className={inputCls} value={date} onChange={(e) => setDate(e.target.value)} required />
            </div>
            <div>
              <label htmlFor="nm-time" className="block text-sm font-medium text-foreground mb-1">Time</label>
              <input id="nm-time" type="time" className={inputCls} value={time} onChange={(e) => setTime(e.target.value)} required />
            </div>
            <div>
              <label htmlFor="nm-dur" className="block text-sm font-medium text-foreground mb-1">Duration</label>
              <select id="nm-dur" className={inputCls} value={duration} onChange={(e) => setDuration(Number(e.target.value))}>
                <option value={15}>15 min</option>
                <option value={30}>30 min</option>
                <option value={60}>1 hour</option>
                <option value={90}>1.5 hours</option>
                <option value={120}>2 hours</option>
              </select>
            </div>
          </div>
          <div>
            <label htmlFor="nm-loc" className="block text-sm font-medium text-foreground mb-1">Location <span className="text-muted-foreground font-normal">(optional)</span></label>
            <input id="nm-loc" className={inputCls} value={location} onChange={(e) => setLocation(e.target.value)} maxLength={200} placeholder="Office / paste a Meet, Teams or Zoom link" />
          </div>

          {!editing && linkProvider && !location.trim() && (
            <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={wantVideoLink}
                onChange={(e) => setWantVideoLink(e.target.checked)}
                className="h-4 w-4 rounded border-border accent-orange-600"
              />
              {linkProvider === 'microsoft' ? 'Add a Microsoft Teams link' : 'Add a Google Meet link'}
              <span className="text-xs text-muted-foreground">
                {orgLinks ? '(company account)' : '(via your connected calendar)'}
              </span>
            </label>
          )}

          {!editing && !linkProvider && tenantProvider && !location.trim() && (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-orange-200 bg-orange-50 dark:border-orange-500/30 dark:bg-orange-500/[0.08] px-3 py-2">
              <p className="text-xs text-foreground m-0">
                Your company uses <strong>{tenantProvider === 'microsoft' ? 'Microsoft Teams' : 'Google Meet'}</strong> for
                meeting links. Connect your {tenantProvider === 'microsoft' ? 'Outlook' : 'Google'} calendar once and
                links are added automatically.
              </p>
              <button
                type="button"
                onClick={connectCalendar}
                className="shrink-0 h-8 px-3 rounded-lg bg-orange-600 text-white hover:bg-orange-700 text-xs font-medium"
              >
                Connect
              </button>
            </div>
          )}

          {/* Organizer: defaults to you; delegation (create) or transfer
              (edit; organizer/creator only) picks another INTERNAL employee —
              they own the meeting, the link and the calendar event. */}
          {(!editing || (attendeesLoaded && myRole !== 'attendee')) && (
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              {editing ? 'Organizer' : 'Organizer'} <span className="text-muted-foreground font-normal">{editing ? '(transfer to a colleague)' : '(scheduling on someone’s behalf?)'}</span>
            </label>
            {organizer ? (
              <span className="inline-flex items-center gap-1 pl-2 pr-1 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700 dark:bg-orange-500/[0.16] dark:text-orange-300">
                {organizer.name || organizer.email}
                <button type="button" aria-label="Reset organizer" onClick={() => { setOrganizer(null); setOrgQuery(''); }} className="hover:text-red-500">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ) : !orgPickerOpen ? (
              <button type="button" onClick={() => setOrgPickerOpen(true)}
                className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-2">
                {editing ? 'Transfer organizer…' : 'Me — click to schedule for someone else'}
              </button>
            ) : (
              <div className="relative">
                <input
                  className={inputCls}
                  value={orgQuery}
                  autoFocus
                  onChange={(e) => setOrgQuery(e.target.value)}
                  placeholder="Search your company…"
                />
                {orgOpts.length > 0 && (
                  <ul className="absolute z-50 mt-1 w-full max-h-44 overflow-auto rounded-lg border border-border bg-card shadow-lg py-1">
                    {orgOpts.map((p) => (
                      <li key={p.email}>
                        <button type="button" className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted"
                          onClick={() => { setOrganizer(p); setOrgPickerOpen(false); setOrgQuery(''); }}>
                          <span className="font-medium text-foreground">{p.name}</span>
                          <span className="ml-2 text-xs text-muted-foreground">{p.email}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
          )}

          {(!editing || attendeesLoaded) && (
          <div>
            <label htmlFor="nm-att" className="block text-sm font-medium text-foreground mb-1">
              {editing ? 'People invited' : 'Invite people'} <span className="text-muted-foreground font-normal">(search your company, or type any email)</span>
            </label>
            <div className="relative">
              <div className={`flex flex-wrap items-center gap-1.5 min-h-9 px-2 py-1.5 rounded-lg border border-border bg-card focus-within:ring-2 focus-within:ring-orange-300`}>
                {attendees.map((a) => (
                  <span key={a.email} className={`inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-xs font-medium ${a.role === 'co_host' ? 'bg-orange-100 text-orange-700 ring-1 ring-orange-400 dark:bg-orange-500/[0.16] dark:text-orange-300' : a.internal ? 'bg-orange-100 text-orange-700 dark:bg-orange-500/[0.16] dark:text-orange-300' : 'bg-muted text-foreground'}`}>
                    {a.name || a.email}{a.role === 'co_host' ? ' · co-host' : ''}
                    <button type="button"
                      title={a.role === 'co_host' ? 'Make participant' : 'Make co-host (can edit this meeting)'}
                      aria-label={`Toggle co-host for ${a.name || a.email}`}
                      onClick={() => setAttendees((prev) => prev.map((p) => p.email === a.email
                        ? { ...p, role: p.role === 'co_host' ? 'participant' : 'co_host' } : p))}
                      className="hover:text-orange-500 font-semibold">
                      ★
                    </button>
                    <button type="button" aria-label={`Remove ${a.name || a.email}`} onClick={() => removeAttendee(a.email)} className="hover:text-red-500">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                <input
                  id="nm-att"
                  ref={inputRef}
                  role="combobox"
                  aria-expanded={optionCount > 0}
                  aria-controls="nm-att-list"
                  aria-autocomplete="list"
                  className="flex-1 min-w-[140px] bg-transparent text-sm text-foreground focus:outline-none py-0.5"
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setFocusIdx(0); }}
                  onKeyDown={onPickerKey}
                  placeholder={attendees.length ? '' : 'Start typing a name or email…'}
                />
              </div>
              {optionCount > 0 && (
                <ul id="nm-att-list" role="listbox" className="absolute z-50 mt-1 w-full max-h-56 overflow-auto rounded-lg border border-border bg-card shadow-lg py-1">
                  {suggestions.map((p, i) => (
                    <li key={p.email} role="option" aria-selected={i === focusIdx}>
                      <button
                        type="button"
                        onClick={() => addAttendee(p)}
                        onMouseEnter={() => setFocusIdx(i)}
                        className={`w-full text-left px-3 py-1.5 text-sm ${i === focusIdx ? 'bg-muted' : ''}`}
                      >
                        <span className="font-medium text-foreground">{p.name}</span>
                        <span className="ml-2 text-xs text-muted-foreground">{p.email}</span>
                      </button>
                    </li>
                  ))}
                  {externalCandidate && (
                    <li role="option" aria-selected={focusIdx === suggestions.length}>
                      <button
                        type="button"
                        onClick={() => addAttendee({ email: externalCandidate })}
                        onMouseEnter={() => setFocusIdx(suggestions.length)}
                        className={`w-full text-left px-3 py-1.5 text-sm ${focusIdx === suggestions.length ? 'bg-muted' : ''}`}
                      >
                        <span className="text-foreground">Invite external:</span>
                        <span className="ml-2 font-medium text-foreground">{externalCandidate}</span>
                      </button>
                    </li>
                  )}
                </ul>
              )}
            </div>
          </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="h-9 px-3 rounded-lg border border-border text-foreground hover:bg-muted text-sm font-medium">Cancel</button>
            <button type="submit" disabled={saving || !title.trim()} className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50 text-sm font-medium">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} {editing ? 'Save changes' : 'Schedule'}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default NewMeetingDialog;
